import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, MessageSquare } from 'lucide-react';
import { Button, LinearProgress } from '@mui/material';

const BlastProcessing = ({ open, onClose, recipientCount, isSuccess, onSuccessClose }) => {
  const [status, setStatus] = useState('processing');
  const [progress, setProgress] = useState(0);

  //* Reset state when modal is opened
  useEffect(() => {
    if (open) {
      setStatus('processing');
      setProgress(0);
    }
  }, [open]);

  //* Update status based on API response
  useEffect(() => {
    if (isSuccess) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setStatus('success');
            return 100;
          }
          return prev + 2;  
        });
      }, 30);

      return () => clearInterval(interval);
    }
  }, [isSuccess]);

  //* Progressive loading simulation while waiting for API
  useEffect(() => {
    if (open && status === 'processing' && !isSuccess) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev < 30) {
            return prev + 3;
          } else if (prev < 50) {
            return prev + 2;
          } else if (prev < 65) {
            return prev + 1;
          } else if (prev < 85) {
            return prev + 0.5;
          } else {
            clearInterval(interval);
              return 85;
          }
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [open, status, isSuccess]);

  if (!open) return null;

  return (
    <AnimatePresence>
      <div className="overflow-y-auto fixed inset-0 z-50">
        <div className="flex justify-center items-center p-4 min-h-screen text-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 backdrop-blur-sm bg-black/30"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="overflow-hidden relative w-full max-w-md bg-white rounded-2xl shadow-xl"
          >
            <AnimatePresence mode="wait">
              {status === 'processing' ? (
                <motion.div
                  key="processing"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-6"
                >
                  {/* Processing Animation */}
                  <div className="relative mx-auto mb-6 w-24 h-24">
                    <motion.div
                      animate={{ 
                        rotate: 360,
                        scale: [1, 1.1, 1],
                      }}
                      transition={{ 
                        rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                        scale: { duration: 1, repeat: Infinity }
                      }}
                      className="flex absolute inset-0 justify-center items-center"
                    >
                      <div className="w-full h-full rounded-full border-4 border-indigo-100 border-t-indigo-500" />
                    </motion.div>
                    <div className="flex absolute inset-0 justify-center items-center">
                      <Send className="w-8 h-8 text-indigo-500 transform -rotate-45" />
                    </div>
                  </div>

                  {/* Processing Content */}
                  <h3 className="mb-2 text-xl font-bold text-gray-900">
                    Sending SMS Blast
                  </h3>
                  <p className="mb-6 text-gray-600">
                    Sending messages to {recipientCount.toLocaleString()} subscribers
                  </p>

                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <LinearProgress 
                      variant="determinate" 
                      value={progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#E0E7FF',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: '#6366F1',
                          borderRadius: 4,
                        },
                      }}
                    />
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Progress</span>
                      <span>{progress}%</span>
                    </div>
                  </div>

                  {/* Message Animation */}
                  <div className="relative mt-6 h-12">
                    <motion.div
                      animate={{
                        x: [-40, 40],
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      className="flex absolute inset-0 justify-center items-center"
                    >
                      <MessageSquare className="w-6 h-6 text-indigo-400" />
                    </motion.div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="p-6"
                >
                  {/* Success Content */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.5 }}
                    className="flex justify-center items-center mx-auto mb-6 w-24 h-24 bg-green-50 rounded-full"
                  >
                    <CheckCircle className="w-12 h-12 text-green-500" />
                  </motion.div>

                  <h3 className="mb-2 text-xl font-bold text-gray-900">
                    SMS Blast Sent Successfully!
                  </h3>
                  <p className="mb-6 text-gray-600">
                    Your message has been sent to {recipientCount.toLocaleString()} subscribers
                  </p>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                      onSuccessClose();
                      window.location.href = '/offers';
                    }}
                    sx={{
                      borderRadius: '12px',
                      backgroundColor: '#22C55E',
                      '&:hover': {
                        backgroundColor: '#16A34A',
                      },
                      py: 1.5,
                    }}
                  >
                    Done
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default BlastProcessing;
